import api from "../utils/api";
import { IAdminOrder } from "../views/AdminOrdersView/types";

const ORDERS_BASE_PATH = "/orders";

export interface IOrderModel {
  // isContactPersonRequired: boolean;
  // isGpsAccurate: boolean;
  // gpsInaccurateDescription?: string;
  // isGatedProperty: boolean;
  // gatedPropertyDetails?: string;
  // tankConnectionDetails: string;
  // tankDistance: string;
  status?: "pending" | "draft" | "confirmed" | "declined" | "modified" | "completed";
}

export interface ICreateOrderArgs {
  // contact details
  name: string;
  phoneNumber: string;
  address: string;
  address2?: string;
  city: string;
  zipCode: string;
  // delivery details
  isContactPersonRequired: boolean;
  isGpsAccurate: boolean;
  gpsInaccurateDescription?: string;
  isGatedProperty: boolean;
  gatedPropertyDetails?: string;
  tankConnectionDetails: string;
  tankDistance: string;
  truckNumber: string;
  service: string;
  // delivery time
  driverId: string;
  startTime: string;
}

export interface IOrderWithLocation extends ICreateOrderArgs {
  // Order identifiers used by list/detail UI.
  _id: string;
  createdAt: string;
  deliveryAt: string;
  // Denormalized payment status comes from Order for fast list rendering.
  paymentStatus?: "pending" | "processing" | "succeeded" | "failed" | "sync_failed";
  paymentMethodType?: "card" | "ach" | "check";
  paidAt?: string;
  status?: "pending" | "draft" | "confirmed" | "declined" | "modified" | "completed";
  canCustomerModify: boolean;
  canCustomerCancel: boolean;
  customerSelfServiceMessage?: string;
}

export interface ICustomerOrderCancellation {
  success: boolean;
  alreadyCancelled: boolean;
  orderId: string;
  status: "declined";
}

export type OrderSortBy = "createdAt" | "deliveryAt";
export type OrderSortOrder = "asc" | "desc";

export interface IDeliveryTime {
  startTime: string;
  driverId: string;
}

export type IAvailableDeliveryTimes = IDeliveryTime[];

export interface IAvailableDeliveryTimesRequest {
  orderId?: string;
  address?: string;
  address2?: string;
  city?: string;
  zipCode?: string;
}

export const OrderModel = {
  // The destructive delete route is retained for the existing admin table only.
  delete: async (orderId: string) => {
    try {
      await api.delete(`${ORDERS_BASE_PATH}/${orderId}`);
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  },
  cancel: async (orderId: string): Promise<ICustomerOrderCancellation> => {
    try {
      const response = await api.post<ICustomerOrderCancellation>(
        `${ORDERS_BASE_PATH}/${orderId}/cancel`
      );
      return response.data;
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  },
  /**
   * Create requests only include customer-entered order data. Persisted fields
   * like _id/createdAt are part of the response/list shape, not the request.
   * @throws {Error}
   */
  create: async (orderArgs: ICreateOrderArgs): Promise<IOrderModel> => {
    try {
      const response = await api.post<IOrderModel>(ORDERS_BASE_PATH, orderArgs);
      return response.data;
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  },
  /**
   * @throws {Error}
   */
  getAll: async (
    page: number = 1,
    limit: number = 10,
    sortBy: OrderSortBy = "deliveryAt",
    sortOrder: OrderSortOrder = "desc"
  ): Promise<{ orders: IOrderWithLocation[], totalCount: number }> => {
    try {
      // Loads paginated order history where payment badges are now also shown.
      const response = await api.get<{ orders: IOrderWithLocation[], totalCount: number }>(
        ORDERS_BASE_PATH,
        {
          params: {
            page,
            limit,
            sortBy,
            sortOrder,
          },
        }
      );
      return {
        orders: response.data.orders || [],
        totalCount: response.data.totalCount || 0
      };
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  },
  /**
   * @throws {Error}
   */
  getAvailableTimes: async (
    request: IAvailableDeliveryTimesRequest = {}
  ): Promise<IAvailableDeliveryTimes> => {
    try {
      const response = await api.get<IAvailableDeliveryTimes>(
        `${ORDERS_BASE_PATH}/availableTimes`,
        { params: Object.keys(request).length > 0 ? request : undefined }
      );
      return response.data || [];
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  },
  createInvoice: async (order: IAdminOrder) => {
    try {
      const response = await api.post<IAdminOrder>(
        `admin/orders/${order._id}/invoice`,
        {
          // Customer identity and invoice amount are resolved from the order on the server.
          truckNumber: order.truckNumber,
        }
      );
      return response.data || [];
    } catch (e: any) {
      console.error(e);
      throw e;
    }
  },
};
